import React, { useState } from 'react'

import {
    Modal,
    Paper,
    Slide,
    Typography,
    IconButton,
    Box,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'

import { Container as CustomContainer, EzosLoader } from '../../../components'
import { PricingTab } from './VendorTab/PricingTab'
import { PendingVendorsPanel } from './PendingVendorsPanel'
import { AddRemoveVendorAptRequest } from '../../../store'
import {
    AreaConfig,
    UnitConfig,
    ListVendor,
    ServiceContractGroup,
    ModelMap,
} from '../../../models'

interface Props {
    open: boolean
    unitConfigs: UnitConfig[]
    areaConfigs: AreaConfig[]
    pendingServiceContractGroups: ServiceContractGroup[]
    selectedPendingVendor: ListVendor | null
    vendorMap: ModelMap<ListVendor>
    onClose: () => void
    onPendingVendorSelect: (vendor: ListVendor) => void
    onClickRequestUpdatedPricing: (vendor: ListVendor) => void
    setPendingServiceContractGroups: (groups: ServiceContractGroup[]) => void
    addVendorToApartment: (req: AddRemoveVendorAptRequest) => Promise<void>
}

export const PendingPricingModal = (props: Props) => {
    const {
        open,
        unitConfigs,
        areaConfigs,
        pendingServiceContractGroups,
        selectedPendingVendor,
        vendorMap,
        onClose,
        onPendingVendorSelect,
        onClickRequestUpdatedPricing,
        setPendingServiceContractGroups,
        addVendorToApartment,
    } = props

    const [isLoading, setIsLoading] = useState(false)

    // Handler for vendor selection with loading
    const handleVendorSelect = (vendor: ListVendor) => {
        setIsLoading(true)
        onPendingVendorSelect(vendor)
        // Simulate loading time for better UX
        setTimeout(() => {
            setIsLoading(false)
        }, 500)
    }

    // Handler for accepting pricing
    const handleAcceptPricing = (vendorId: number) => {
        if (!vendorMap[vendorId]) {
            addVendorToApartment({
                body: {
                    vendor: vendorId,
                },
            })
        }
        setPendingServiceContractGroups(
            pendingServiceContractGroups.map((group) =>
                group.vendor.id === vendorId
                    ? { ...group, status: 'CURRENT' as any }
                    : group,
            ),
        )
    }

    // Handler for rejecting pricing
    const handleRejectPricing = (vendorId: number) => {
        setPendingServiceContractGroups(
            pendingServiceContractGroups.map((group) =>
                group.vendor.id === vendorId
                    ? { ...group, status: 'REJECTED' as any }
                    : group,
            ),
        )
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={open} mountOnEnter unmountOnExit>
                <Paper
                    style={{
                        width: '80%',
                        height: '85%',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {/* Header - spans full width */}
                    <div
                        style={{
                            borderBottom: '1px solid #e0e0e0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            height: '64px',
                            minHeight: '64px',
                            width: '100%',
                            paddingLeft: '16px',
                            paddingRight: '16px',
                            boxSizing: 'border-box',
                        }}
                    >
                        <Typography variant="h6" style={{ fontWeight: 600 }}>
                            Pending Pricing Requests
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={onClose}
                            style={{ marginLeft: '16px' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>

                    {/* Main Content Area */}
                    {pendingServiceContractGroups.length === 0 ? (
                        <Box
                            style={{
                                height: 'calc(100% - 64px)',
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '64px',
                                textAlign: 'center',
                            }}
                        >
                            <Typography
                                variant="h4"
                                style={{
                                    color: '#666',
                                    marginBottom: '16px',
                                    fontWeight: 500,
                                }}
                            >
                                No Pending Requests
                            </Typography>
                            <Typography
                                variant="h6"
                                style={{
                                    color: '#999',
                                    lineHeight: 1.6,
                                    maxWidth: '500px',
                                }}
                            >
                                There are currently no pending pricing requests
                                to review. All vendor pricing requests have been
                                processed.
                            </Typography>
                        </Box>
                    ) : (
                        <CustomContainer
                            direction="row"
                            style={{
                                height: 'calc(100% - 64px)',
                                width: '100%',
                            }}
                        >
                            {/* Pending Vendors Panel */}
                            <PendingVendorsPanel
                                pendingServiceContractGroups={
                                    pendingServiceContractGroups
                                }
                                selectedVendor={selectedPendingVendor}
                                onVendorSelect={handleVendorSelect}
                            />

                            {/* Main Content - Only Pricing Tab */}
                            <CustomContainer
                                direction="column"
                                style={{
                                    height: '100%',
                                    flex: 1,
                                }}
                            >
                                {isLoading ? (
                                    <EzosLoader />
                                ) : selectedPendingVendor ? (
                                    <PricingTab
                                        vendor={selectedPendingVendor}
                                        unitConfigs={unitConfigs}
                                        areaConfigs={areaConfigs}
                                        onClickRequestUpdatedPricing={
                                            onClickRequestUpdatedPricing
                                        }
                                        onAcceptPricing={handleAcceptPricing}
                                        onRejectPricing={handleRejectPricing}
                                    />
                                ) : (
                                    <Box
                                        style={{
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '64px',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            style={{
                                                color: '#666',
                                                marginBottom: '8px',
                                                fontWeight: 500,
                                            }}
                                        >
                                            Select a Vendor
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            style={{
                                                color: '#999',
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            Choose a vendor from the list to
                                            review their pricing details.
                                        </Typography>
                                    </Box>
                                )}
                            </CustomContainer>
                        </CustomContainer>
                    )}
                </Paper>
            </Slide>
        </Modal>
    )
}
